/**
 * Send the current user's details to Ulgebra's customer-success team via the
 * `customer_request_query` WhatsApp template. Mirrors the legacy
 * `sendCustomerDetailsForUlgebraWhatsapp` from
 * `public/js/firebase-things.js:35296` — same endpoint, same template name,
 * same parameter order so the message format remains identical.
 *
 * Used by the "Contact Ulgebra to Setup" button on the WhatsApp embedded-signup
 * screen. Shipping a typed React port instead of leaving it as a vestigial JS
 * call so callers can `await` and react to success/failure.
 */
import { callServiceAPI } from '@/lib/firebase/functions';
import { getDocument } from '@/lib/firebase/firestore';
import { COLLECTIONS, getTPADisplayName } from '@/config/constants';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { useStoreStore } from '@/stores/store-store';

/** Fixed Ulgebra-owned WABA sender. Matches the legacy hard-coded ID so the
 *  CF routes the request through the same business account. */
const ULGEBRA_WHATSAPP_SENDER_ID = '101098002784609';

/** Subset of `ulgebraUsers/{uid}` doc fields we read for the template. */
interface UlgebraUserDoc {
  ph?: string;
  appUsCount?: number | string;
}

export interface ContactUlgebraResult {
  ok: boolean;
  /** Surfaced to the user when `ok` is false. */
  error?: string;
}

interface ContactUlgebraInput {
  /** User-supplied WhatsApp number. If omitted, falls back to whatever is
   *  stored on the `ulgebraUsers/{uid}` doc — and errors out if neither is
   *  available, since the template needs a recipient. */
  phone?: string;
}

/** Resolve "does the user have any active paid plan" without forcing a load —
 *  if subscriptions haven't been fetched yet we conservatively return false,
 *  same as the legacy default. */
function hasAnyValidPlan(): boolean {
  const subs = useStoreStore.getState().subscriptions;
  return subs.some((s) => s.status === 'active' || s.status === 'trialing');
}

export async function sendCustomerDetailsForUlgebraWhatsapp(
  input: ContactUlgebraInput = {}
): Promise<ContactUlgebraResult> {
  const auth = useAuthStore.getState();
  const app = useAppStore.getState();

  const user = auth.user;
  if (!user) return { ok: false, error: 'Not signed in' };

  const extensionName = app.extensionName;
  const appCodeTPA = app.appCodeTPA;
  const appCodeSAAS = app.appCodeSAAS;
  if (!extensionName || !appCodeTPA || !appCodeSAAS) {
    return { ok: false, error: 'App context not initialized' };
  }

  // Resolve recipient phone — explicit arg wins, then user doc, then bail.
  let recipientPhone = (input.phone || '').trim();
  let appUsCount: number | string = 0;
  try {
    const userDoc = await getDocument<UlgebraUserDoc>(COLLECTIONS.USERS, user.uid);
    if (!recipientPhone && userDoc?.ph) recipientPhone = String(userDoc.ph).trim();
    if (userDoc?.appUsCount != null) appUsCount = userDoc.appUsCount;
  } catch (err) {
    console.warn('[contact-ulgebra] Failed to read user doc:', err);
  }

  if (!recipientPhone) {
    return { ok: false, error: 'WhatsApp number is required' };
  }

  const appPrettyName = getTPADisplayName(appCodeTPA);
  const validPlan = hasAnyValidPlan();

  // Template payload mirrors legacy 1:1 — Ulgebra's CF expects this exact
  // shape (template name, language, ordered parameters).
  const messagePayload = {
    messaging_product: 'whatsapp',
    recipient_type: 'individual',
    whatsAppSenderID: ULGEBRA_WHATSAPP_SENDER_ID,
    recipientPhoneNumber: recipientPhone,
    template: {
      name: 'customer_request_query',
      language: { code: 'en' },
      components: [
        {
          type: 'body',
          parameters: [
            { type: 'text', text: String(appPrettyName) },
            { type: 'text', text: `${appCodeTPA}for${appCodeSAAS}` },
            { type: 'text', text: String(appUsCount) },
            { type: 'text', text: recipientPhone },
            { type: 'text', text: String(user.email || '') },
            { type: 'text', text: String(validPlan) },
          ],
        },
      ],
    },
  };

  // TPA service name is derived from the extension name — same convention as
  // `processEmbeddedSignup` / `useDashboard`. The CF needs it to load the
  // correct stored credentials when relaying the template through Ulgebra's
  // own WABA sender.
  const tpaService = extensionName.includes('for')
    ? extensionName.split('for')[0]
    : extensionName;

  try {
    const result = await callServiceAPI({
      appCode: extensionName,
      TPAService: tpaService,
      url: 'sendCustomerDetailsForUlgebraWhatsapp',
      method: 'post',
      data: messagePayload,
    });
    console.log('[contact-ulgebra] Sent customer details', result);
    return { ok: true };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Failed to contact Ulgebra';
    console.error('[contact-ulgebra] Failed:', err);
    return { ok: false, error: msg };
  }
}
